import React, { useMemo, useState } from "react";
import { Minus, Plus, Printer, X } from "lucide-react";
import { canEncodeCode128B, renderCode128SvgBars } from "../../../utils/code128";
import { printBarcodeLabel } from "../../../services/printerApi";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";

const formatPrice = (amount) => {
    const value = Number(amount || 0);
    return new Intl.NumberFormat("en-PH", {
        style: "currency",
        currency: "PHP",
        minimumFractionDigits: 2,
    }).format(Number.isFinite(value) ? value : 0);
};

const DEFAULT_LABEL_WIDTH_MM = 50;
const DEFAULT_LABEL_HEIGHT_MM = 30;
const DEFAULT_BARCODE_HEIGHT_MM = 18;

const clampNumber = (value, min, max, fallback) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return fallback;
    return Math.min(max, Math.max(min, numeric));
};

function BarcodeSvg({ value, className = "", style }) {
    const barcode = useMemo(() => renderCode128SvgBars(value), [value]);

    return (
        <svg
            className={className}
            style={style}
            viewBox={`0 0 ${barcode.width} ${barcode.height}`}
            role="img"
            aria-label={`Barcode ${value}`}
            preserveAspectRatio="xMidYMid meet"
        >
            <rect width={barcode.width} height={barcode.height} fill="#fff" />
            {barcode.bars.map((bar) => (
                <rect key={bar.key} x={bar.x} y={bar.y} width={bar.width} height={bar.height} fill="#020617" />
            ))}
            <text
                x={barcode.width / 2}
                y={barcode.barHeight + 17}
                textAnchor="middle"
                fontFamily="Arial, sans-serif"
                fontSize="14"
                fontWeight="700"
                fill="#020617"
            >
                {value}
            </text>
        </svg>
    );
}

function NumberStepper({ label, value, onChange, min, max, fallback, ariaLabel }) {
    const normalizedValue = Math.round(clampNumber(value, min, max, fallback));
    const updateValue = (nextValue) => {
        const cleanValue = String(nextValue || "").replace(/\D/g, "").slice(0, 2);
        onChange(cleanValue);
    };
    const stepValue = (delta) => {
        onChange(String(Math.round(clampNumber(Number(value || fallback) + delta, min, max, fallback))));
    };

    return (
        <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</span>
            <div className="grid grid-cols-[44px_1fr_44px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-with-theme hover:border-[var(--color-primary)] focus-within:border-[var(--color-primary)] focus-within:ring-2 focus-within:ring-[var(--color-primary)]/15">
                <button
                    type="button"
                    onClick={() => stepValue(-1)}
                    className="flex h-11 items-center justify-center border-r border-slate-200 text-slate-500 transition hover:bg-slate-50"
                    aria-label={`Decrease ${ariaLabel || label}`}
                >
                    <Minus size={16} />
                </button>
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={value}
                    onChange={(event) => updateValue(event.target.value)}
                    onBlur={() => onChange(String(normalizedValue))}
                    className="h-11 w-full border-0 bg-white px-3 text-center text-base font-bold text-slate-800 outline-none"
                    aria-label={ariaLabel || label}
                />
                <button
                    type="button"
                    onClick={() => stepValue(1)}
                    className="flex h-11 items-center justify-center border-l border-slate-200 text-slate-500 transition hover:bg-slate-50"
                    aria-label={`Increase ${ariaLabel || label}`}
                >
                    <Plus size={16} />
                </button>
            </div>
        </label>
    );
}

export default function BarcodePrintModal({ product, onClose }) {
    const [labelWidthMm, setLabelWidthMm] = useState(String(DEFAULT_LABEL_WIDTH_MM));
    const [labelHeightMm, setLabelHeightMm] = useState(String(DEFAULT_LABEL_HEIGHT_MM));
    const [barcodeHeightMm, setBarcodeHeightMm] = useState(DEFAULT_BARCODE_HEIGHT_MM);
    const [copies, setCopies] = useState("1");
    const [printing, setPrinting] = useState(false);
    const barcodeValue = String(product?.product_code || product?.barcode || "").trim();
    const isBarcodeValid = Boolean(barcodeValue) && canEncodeCode128B(barcodeValue);
    const canPrint = isBarcodeValid && !printing;
    const normalizedLabelWidthMm = Math.round(clampNumber(labelWidthMm, 30, 80, DEFAULT_LABEL_WIDTH_MM));
    const normalizedLabelHeightMm = Math.round(clampNumber(labelHeightMm, 20, 60, DEFAULT_LABEL_HEIGHT_MM));
    const maxBarcodeHeightMm = Math.max(12, Math.min(26, normalizedLabelHeightMm - 10));
    const normalizedBarcodeHeightMm = Math.round(clampNumber(barcodeHeightMm, 12, maxBarcodeHeightMm, DEFAULT_BARCODE_HEIGHT_MM));
    const normalizedCopies = Math.round(clampNumber(copies, 1, 99, 1));

    const updateCopies = (nextValue) => {
        const value = String(nextValue || "").replace(/\D/g, "").slice(0, 2);
        setCopies(value);
    };

    const stepCopies = (delta) => {
        setCopies(String(Math.round(clampNumber(Number(copies || 1) + delta, 1, 99, 1))));
    };

    const handlePrint = async () => {
        if (!canPrint) return;

        setPrinting(true);
        try {
            const response = await printBarcodeLabel({
                product_id: product?.id,
                name: product?.name || "Product",
                barcode: barcodeValue,
                price: product?.price || 0,
                label_width_mm: normalizedLabelWidthMm,
                label_height_mm: normalizedLabelHeightMm,
                barcode_height_mm: normalizedBarcodeHeightMm,
                copies: normalizedCopies,
            });
            showSuccessToast(response.data?.message || "Barcode label queued.");
        } catch (error) {
            showErrorToast(error.response?.data?.message || "Barcode label printing failed.");
        } finally {
            setPrinting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-slate-950/60 p-3 backdrop-blur-sm sm:p-4">
            <div className="my-4 w-full max-w-md overflow-hidden rounded-[28px] bg-white shadow-2xl sm:my-6">
                <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">Barcode Label</p>
                        <h2 className="mt-1 text-xl font-bold text-slate-950">Print Barcode</h2>
                        <p className="mt-1 text-sm text-slate-500">Generate a Code 128 label for a barcode printer.</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50"
                        aria-label="Close barcode print modal"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5">
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                        <div className="rounded-2xl bg-white p-4 text-center shadow-sm">
                            <div className="mx-auto mb-3 inline-flex rounded-full bg-sky-50 px-3 py-1 text-[11px] font-bold text-sky-700">
                                Xprinter 236B preset: {normalizedLabelWidthMm}mm x {normalizedLabelHeightMm}mm
                            </div>
                            <p className="truncate text-sm font-bold text-slate-950">{product?.name || "Product"}</p>
                            {isBarcodeValid ? (
                                <BarcodeSvg value={barcodeValue} className="mt-3 w-full" style={{ height: `${normalizedBarcodeHeightMm * 4}px` }} />
                            ) : (
                                <div className="mt-3 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm font-semibold text-amber-700">
                                    Barcode is empty or contains unsupported characters.
                                </div>
                            )}
                            <p className="mt-2 text-sm font-bold text-slate-900">{formatPrice(product?.price)}</p>
                        </div>
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="grid gap-3 sm:grid-cols-2">
                            <NumberStepper
                                label="Label Width"
                                value={labelWidthMm}
                                onChange={setLabelWidthMm}
                                min={30}
                                max={80}
                                fallback={DEFAULT_LABEL_WIDTH_MM}
                                ariaLabel="Label width"
                            />
                            <NumberStepper
                                label="Label Height"
                                value={labelHeightMm}
                                onChange={setLabelHeightMm}
                                min={20}
                                max={60}
                                fallback={DEFAULT_LABEL_HEIGHT_MM}
                                ariaLabel="Label height"
                            />
                        </div>
                        <label className="mt-4 block">
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Barcode Height</span>
                                <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-slate-600 shadow-sm">{normalizedBarcodeHeightMm}mm</span>
                            </div>
                            <input
                                type="range"
                                min="12"
                                max={maxBarcodeHeightMm}
                                value={normalizedBarcodeHeightMm}
                                onChange={(event) => setBarcodeHeightMm(Number(event.target.value))}
                                className="w-full accent-sky-700"
                            />
                        </label>
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <label className="block">
                            <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Copies</span>
                            <div className="grid grid-cols-[44px_1fr_44px] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-with-theme hover:border-[var(--color-primary)] focus-within:border-[var(--color-primary)] focus-within:ring-2 focus-within:ring-[var(--color-primary)]/15">
                                <button
                                    type="button"
                                    onClick={() => stepCopies(-1)}
                                    className="flex h-11 items-center justify-center border-r border-slate-200 text-slate-500 transition hover:bg-slate-50"
                                    aria-label="Decrease copies"
                                >
                                    <Minus size={16} />
                                </button>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={copies}
                                    onChange={(event) => updateCopies(event.target.value)}
                                    onBlur={() => setCopies(String(normalizedCopies))}
                                    className="h-11 w-full border-0 bg-white px-3 text-center text-base font-bold text-slate-800 outline-none"
                                    aria-label="Copies"
                                />
                                <button
                                    type="button"
                                    onClick={() => stepCopies(1)}
                                    className="flex h-11 items-center justify-center border-l border-slate-200 text-slate-500 transition hover:bg-slate-50"
                                    aria-label="Increase copies"
                                >
                                    <Plus size={16} />
                                </button>
                            </div>
                        </label>
                    </div>

                    <div className="mt-3 rounded-2xl bg-slate-50 px-4 py-3 text-xs text-slate-500">
                        Sends a TSPL/TSPL2 label job to the Android Print Bridge. Configure the XP-236B transport in the bridge app as USB, Bluetooth, or Network.
                    </div>

                    <div className="mt-5 grid gap-3 sm:grid-cols-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex h-12 items-center justify-center rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-600 transition hover:bg-slate-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handlePrint}
                            disabled={!canPrint}
                            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ background: "var(--color-primary)" }}
                        >
                            <Printer size={17} />
                            {printing ? "Printing..." : "Print Label"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
